import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import StringResolutionManager from 'models/managers/StringResolutionManager';
import {ClientId} from 'notebook/ELNConstants';
import {
  InventoryStructuredTable,
  InventoryTableRowMetadata,
} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {InventoryTableMetadata} from 'notebook/integrations/samples/InventoryTableConstants';
import {ACTION_INVENTORY_SUBMIT} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  abortAction,
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {
  StructuredTable,
  StructuredTableRow,
  StructuredTableKind,
  StructuredTableMetadata,
  TableActionFunctionParameters,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
  StructuredTableSchema,
  GenericStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {getStorableInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import notifications from 'notifications';
import {DataModsItemType} from 'samples/getInventoryRegistryChangeSummary';
import * as StorableActions from 'samples/StorableActions';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';

import {getRowAwareLoadingMessage, RowSubmissionRange} from './makeSubmitRowActionFunction';

interface InventorySubmitErrorsModalProps {
  errorData: SpreadsheetValidationErrorInfo;
  onRequestClose: () => void;
}

export function InventorySubmitErrorsModal(props: InventorySubmitErrorsModalProps): JSX.Element {
  return (
    <Modal
      enableRedesign={true}
      title="Errors when submitting inventory table"
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
      ]}
    >
      <ValidationErrorsTable
        spreadsheetType={SpreadsheetType.STRUCTURED_TABLE}
        errorData={props.errorData}
        resolvableErrorStatuses={{}}
      />
    </Modal>
  );
}

async function attemptBatchedInventorySubmit(
  entryId: string,
  structuredTable: InventoryStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.InventorySubmitResponse>> {
  const responseData: ActionFunctionDAO.InventorySubmitResponse = {containers: [], entryVersionId: -1};
  let latestVersionId: number | undefined;
  let partialSuccess = false;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const batchSize = getSubmissionBatchSize(StructuredTableKind.INVENTORY);
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  try {
    for (const {range, evaluatedRowBatch} of batches) {
      if (batchSize && updateStatus) {
        updateStatus(
          getRowAwareLoadingMessage(range, 'Updating Results For Rows', 'Updating Results For Row')
        );
      }
      const batchResult = await ActionFunctionDAO.inventorySubmit(
        entryId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        range,
        latestVersionId,
        dataModsHeader
      );

      latestVersionId = batchResult.entryVersionId;
      responseData.containers.push(...batchResult.containers);
      submittedRange = {...submittedRange, end: range.end};
      partialSuccess = true;
    }
  } catch (e) {
    if (e.responseJSON?.bulkAdapterErrorInfo) {
      await LoadingScreen.withLoadingScreenHidden(() =>
        legacyRunModal<InventorySubmitErrorsModalProps>(InventorySubmitErrorsModal, {
          errorData: e.responseJSON.bulkAdapterErrorInfo,
        })
      );
      if (partialSuccess) {
        return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
      } else {
        return abortAction();
      }
    }
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }
    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformInventorySubmit(
  entryId: string,
  structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.InventorySubmitResponse>> {
  const inventorySubmitResult = await attemptBatchedInventorySubmit(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    updateStatus,
    dataModsHeader
  );

  if (inventorySubmitResult.resultType === ActionResultType.ABORTED) {
    return inventorySubmitResult;
  }

  // Invalidate the cached custom eval for containers that were modified so new rows don't show erroneous data
  const containerIds = inventorySubmitResult.responseData.containers.map((container) => container.id);
  [
    CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_CONTAINER,
    CustomResolutionQueryType.INVENTORY_TABLE_QUANTITY_AMOUNT_FROM_SOURCE_PLATE,
  ].forEach((queryType) => {
    StringResolutionManager.clearStringResolutionReduxCacheByTypeAndId(queryType, containerIds);
  });

  return inventorySubmitResult;
}

export function getSampleIdsForStatusWarningFromStructuredTable({
  structuredTableRows,
  structuredTableColumns,
  structuredTableSchemas,
  computedTable,
}: {
  structuredTableRows: Array<StructuredTableRow<InventoryTableRowMetadata>>;
  structuredTableColumns: Array<GenericStructuredTableColumn<ClientId>>;
  structuredTableSchemas: Array<StructuredTableSchema>;
  computedTable: EvaluatedTable;
}): Array<string> {
  const ids = computedTable.flatMap((row, rowIndex) => {
    const storableIds: Array<string> = [];
    if (
      rowHasUnsubmittedChanges(
        row,
        structuredTableRows[rowIndex],
        structuredTableColumns,
        structuredTableSchemas
      )
    ) {
      for (const [colIndex, {columnType}] of structuredTableColumns.entries()) {
        const typedValue = row[colIndex].value ?? structuredTableRows[rowIndex].cells[colIndex].typedValue;
        const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
        if (!storableInfo) {
          continue;
        }
        if ([TableColumnType.CONTAINERS, TableColumnType.PLATE].includes(columnType)) {
          storableIds.push(storableInfo.id);
        }
      }
    }
    return storableIds;
  });
  return ids;
}

/**
 * Get items being modified by the table submission to use for data mods.
 *
 * This is used to trigger data mods on every submit of inventory update tables, rather than just on
 * table resubmission.
 *
 * Note: This potentially returns duplicate plate ids for update plate well tables, because we do not have
 * the container ids. The duplicates are returned to get an accurate well count, while they will be
 * de-duplicated before checking for data mods. This does mean that the data mods checks are checking the
 * plate's project, not the wells. This is fine right now since these tables only support well plates, but
 * if they are extended to matrix plates then we'll need to find a way to check the containers specifically.
 *
 * @param structuredTables Structured tables being submitted; currently only supports generating change counts
 * for inventory update tables.
 * @param structuredTableAdapters Corresponding adapters, one per structured table
 * @param computedTables Corresponding computed tables, one per structured table
 *
 * @returns Mapping from data mods item type to the corresponding api ids.
 */
export function getInventoryDataMods(
  structuredTables: Array<UnknownStructuredTable>,
  structuredTableAdapters: Array<UnknownStructuredTableAdapter>,
  computedTables: Array<EvaluatedTable>
): {[k: string]: Array<string>} {
  const itemChangeIds: {[k: string]: Array<string>} = {};
  const isSubmittingInventoryUpdateTables = structuredTables.every(
    (table) =>
      table.kind === StructuredTableKind.INVENTORY &&
      (table.metadata as StructuredTableMetadata & InventoryTableMetadata).mode ===
        InventoryStructuredTableMode.UPDATE_CONTAINERS
  );
  if (isSubmittingInventoryUpdateTables) {
    const primarySamples = structuredTableAdapters.flatMap((adapter, idx) => {
      const structuredTable = structuredTables[idx];
      const orderedPrimarySamples = Object.values(
        adapter.getPrimarySamples(structuredTable, computedTables[idx])
      );
      // This generally just means unfinalized rows, but it also takes into account the `unlockRowsAfterSubmit`
      // metadata value to include finalized rows with changes being submitted
      const rowUnsubmittedStatuses = adapter.getRowUnsubmittedStatuses(structuredTable, computedTables[idx]);
      return orderedPrimarySamples.filter((sample, rowIdx) => rowUnsubmittedStatuses[rowIdx]).flat();
    });
    itemChangeIds[DataModsItemType.CONTAINER_OR_PLATE_WELL] = primarySamples
      .filter(({sampleLookupInfo}) => sampleLookupInfo != null)
      .map(({sampleLookupInfo}) => sampleLookupInfo!.idForLookup);
  }
  return itemChangeIds;
}

export async function onInvokeInventorySubmitActionFunction(
  actionFunctionParams: TableActionFunctionParameters<InventoryTableMetadata, {}>
): Promise<void> {
  const {entryOrTemplateId, structuredTable, structuredTableAdapter, computedTable, actionFunctionContext} =
    actionFunctionParams;
  assert(
    isInExecutableCanvas(actionFunctionParams),
    'Inventory submit action is only supported in entries and executable documents.',
    {apiId: entryOrTemplateId}
  );
  assert(entryOrTemplateId != null, 'Document ID not provided');

  const sampleIds = getSampleIdsForStatusWarningFromStructuredTable({
    structuredTableRows: structuredTable.rows,
    structuredTableColumns: structuredTable.columns,
    structuredTableSchemas: structuredTable.schemas,
    computedTable,
  });
  const {shouldProceed} = await callSampleStatusWarningModal({
    sampleIds: [...new Set(sampleIds)],
    trackingParams: {
      actionOrigin: structuredTableActionOrigin(structuredTable),
      actionAttempted: 'update_quantity',
      eventGroupId: Analytics.makeAnalyticsEventId(),
    },
    canSelectMultiple: false,
    showOnlyWarnings: true,
  });

  if (shouldProceed) {
    await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.InventorySubmitResponse>({
      entryId: entryOrTemplateId,
      loadingScreenMessage: 'Updating...',
      autoSaverAction: ACTION_INVENTORY_SUBMIT,
      dataModsItemFn: getInventoryDataMods,
      doPerformAction: ({updateStatus, dataModsHeader}) =>
        doPerformInventorySubmit(
          entryOrTemplateId,
          structuredTable,
          computedTable,
          actionFunctionContext,
          undefined,
          updateStatus,
          dataModsHeader
        ),
      doPostRefresh: (actionResult) => {
        StorableActions.dispatchStorablesLoaded(actionResult.responseData.containers);
        invokeStructuredTableMappings(
          entryOrTemplateId,
          structuredTable,
          // on a partial success we want to map only the rows that were successfully submitted
          actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
            ? actionResult.submittedRange
            : undefined
        );
        return Promise.resolve();
      },
      analyticsActionForPerfTracking:
        ELN_ANALYTICS_ACTIONS.UPDATED_CONTAINERS_OR_WELLS_FROM_INVENTORY_TABLE.action,
      structuredTables: [structuredTable],
      structuredTableAdapters: [structuredTableAdapter],
      computedTables: [computedTable],
      tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
    });
  }
}

export const inventorySubmitActionFunction = makeSimpleActionFunction<InventoryTableMetadata, {}>({
  label: (params) => getSubmitButtonLabel(params.tableStatus),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeInventorySubmitActionFunction,
  getDisabled: (params) => isTableActionButtonDisabled(params),
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
